import React from 'react';
import PropTypes from 'prop-types';
import { SFEntityID } from 'utils/types';
import { Link } from 'react-router-dom';
import { Card } from 'semantic-ui-react';
import css from './ServicesCard.scss';
import ServicesCardFooter from './ServicesCardFooter';
import ServicesCardHeader from './ServicesCardHeader';
import ServicesCardTopBar from './ServicesCardTopBar';

import { debounce } from 'lodash';
import { camelCase } from 'lodash';
import striptags from 'striptags';

// Hard-coded constants. This could be on CSS, but we are using it here to computate text's heights
// programatically
const DESC_LINE_HEIGHT = 24;
const HEADER_LINE_HEIGHT = 32;

export default class ServicesCard extends React.Component {

  constructor(props) {
    super(props);
    this.state = { description: this.parseHTML(props.serviceDescription) };

    // Check for clipping at the end of the resizing
    this.clipDescription = debounce(this.clipDescription.bind(this), 500);
  }

  componentDidMount() {
    this.clipDescription();
    window.addEventListener("resize", this.clipDescription, false);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.clipDescription, false);
  }

  // If service description ever changes, re-evaluate it to check if any clipping is needed
  componentDidUpdate(prevProps) {
    if ( this.props.serviceDescription !== prevProps.serviceDescription ) this.clipDescription();
  }

  parseHTML(string) {
    // As of https://jira.hobsons.com/browse/SD-6751 we allow descriptions to contain HTML
    // but we don't want HTML in this card (it potentially can mess a lot with the grid layout)
    // nor line breaks
    return striptags(string).replace(/\n/ig, ' ');
  }

  get headerDivId() {
    return `service-catalog-card-header-${this.props.id}`;
  }

  get descriptionDivId() {
    return `service-catalog-card-desc-${this.props.id}`;
  }

  numberOfLines(headerLines) {
    // We want to have at most 10 lines of description in the best case (everything fits well)
    // If hour header is too big, we will need to reduce this number in order to still fit the
    // description in the card
    const EXPECTED_NUMBER_OF_LINES = 10;

    let penalty = 0;
    switch ( headerLines ) {
      case 1:
        break; // Header fits in single line, no need for penalty
      case 2:
        penalty = 1; // Header is big, drop a line from our expected number of lines
        break;
      case 3:
        penalty = 3; // Unlikely scenario, give a bigger penalty for security
        break;
      default:
        penalty = 5; // Shouldn't get this far, it's only a fallback for big crazy headers
        break;
    }
    return EXPECTED_NUMBER_OF_LINES - penalty;
  }

  computeMaxHeight() {
    // Define number of lines based on header
    const header = document.getElementById(this.headerDivId);
    const headerLines = header.clientHeight / HEADER_LINE_HEIGHT;

    // Define the max height for the description, i.e., above that we will
    // start to clip the text
    return DESC_LINE_HEIGHT * this.numberOfLines(headerLines);
  }

  // Computes which would be the height of a clone DOM element with the same width
  // of a given element, but with a given innerText
  computeNewHeight(element, innerText) {
    const clone = element.cloneNode();
    clone.innerText = innerText;
    clone.style.visibility = "hidden";
    clone.style.width = element.clientWidth + "px";
    document.body.appendChild(clone);
    const newHeight = clone.clientHeight;
    clone.remove(); // Make sure we don't leave any garbage
    return newHeight;
  }

  clipDescription() {
    const serviceDescription = this.parseHTML(this.props.serviceDescription);
    const element = document.getElementById(this.descriptionDivId);
    if ( element === null ) return;

    const maxHeight = this.computeMaxHeight();
    const height = this.computeNewHeight(element, serviceDescription);
    if ( height <= maxHeight ) { // No overflow, no need to clip
      this.setState({description: serviceDescription});
      return;
    }

    // Split the words and iterate until we overflow
    const words = serviceDescription.split(" ");
    let count = 1;
    while ( count <= words.length ) {
      const innerText = words.slice(0, count).join(" ");
      const newHeight = this.computeNewHeight(element, innerText);
      if ( newHeight > maxHeight ) break;
      count += 1;
    }

    // Here we've found where to split. We subtract 2 words to have a
    // safe margin and also look for non-ending chars to avoid weird
    // positioned ellipsis.
    const text = words.slice(0, count - 3).join(" ");
    const lastChar = text.slice(-1);
    const nonEndingChars = [".", ",", "?", ";", "!", " "];
    const clippedText = nonEndingChars.indexOf(lastChar) > -1
      ? `${text.slice(0, text.length - 1)}...`
      : `${text}...`;

    // Finally set the new clipped text
    this.setState({description: clippedText});
  }

  render() {
    const {
      id, serviceUuid, name, kioskQueueSize, kioskMaxWaitTime, onlineSchedulingAvailable, serviceCalendarEnabled,
      phoneNumber, kioskId, displayStudentQueue, isServiceMember, isStaff, isStudent, displaySmartThinkingLink,
      canSeeStudentServices, hasConnectLicense, canMakeAppointment, email, url, kioskType, virtualCheckInEnabled,
      checkedIntoService, virtualCheckInOpen
    } = this.props;
    return (
      <Card fluid id={camelCase("serviceCard " + name)}>
        <ServicesCardTopBar serviceUuid={serviceUuid} />
        <Card.Content className={css['card-content-container']}>
          <ServicesCardHeader
            serviceId={id}
            serviceUuid={serviceUuid}
            serviceName={name}
            kioskQueueSize={kioskQueueSize}
            kioskMaxWaitTime={kioskMaxWaitTime}
            kioskId={kioskId}
            displayStudentQueue={displayStudentQueue}
            isServiceMember={isServiceMember}
            kioskType={kioskType}
            isStudent={isStudent}
            virtualCheckInEnabled={virtualCheckInEnabled}
            checkedIntoService={checkedIntoService}
            virtualCheckInOpen={virtualCheckInOpen}
          />
          <Card.Content className={css['card-content']}>
            <Card.Header
              id={this.headerDivId}
              style={{lineHeight: `${HEADER_LINE_HEIGHT}px`}}
              className={`${css['card-title']} card-title`}>
              <Link to={`/service/${id}`}>{name}</Link>
            </Card.Header>
            <Card.Description
              id={this.descriptionDivId}
              className={css['card-desc']}
              style={{lineHeight: `${DESC_LINE_HEIGHT}px`}}>
              <Link to={`/service/${id}`}>{this.state.description}</Link>
            </Card.Description>
          </Card.Content>
        </Card.Content>
        <Card.Content extra>
          <ServicesCardFooter
            id={id}
            isStaff={isStaff}
            isStudent={isStudent}
            onlineSchedulingAvailable={onlineSchedulingAvailable}
            serviceCalendarEnabled={serviceCalendarEnabled}
            phoneNumber={phoneNumber}
            name={name}
            displaySmartThinkingLink={displaySmartThinkingLink}
            canSeeStudentServices={canSeeStudentServices}
            canMakeAppointment={canMakeAppointment}
            hasConnectLicense={hasConnectLicense}
            email={email}
            url={url}
            kioskId={kioskId}
            virtualCheckInEnabled={virtualCheckInEnabled}
            checkedIntoService={checkedIntoService}
            virtualCheckInOpen={virtualCheckInOpen}
          />
        </Card.Content>
      </Card>
    );
  }
}

ServicesCard.propTypes = {
  id: SFEntityID.isRequired,
  serviceUuid: PropTypes.string,
  name: PropTypes.string.isRequired,
  serviceDescription: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  kioskQueueSize: PropTypes.number,
  kioskMaxWaitTime: PropTypes.number,
  onlineSchedulingAvailable: PropTypes.bool,
  serviceCalendarEnabled: PropTypes.bool,
  kioskId: PropTypes.number,
  displayStudentQueue: PropTypes.bool,
  isStaff: PropTypes.bool.isRequired,
  isServiceMember: PropTypes.bool.isRequired,
  isStudent: PropTypes.bool.isRequired,
  displaySmartThinkingLink: PropTypes.bool,
  canSeeStudentServices: PropTypes.bool,
  canMakeAppointment: PropTypes.bool,
  hasConnectLicense: PropTypes.bool,
  email: PropTypes.string,
  url: PropTypes.string,
  kioskType: PropTypes.string,
  virtualCheckInEnabled: PropTypes.bool,
  checkedIntoService: PropTypes.bool,
  virtualCheckInOpen: PropTypes.bool
};
